import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Plane, MapPin, Calendar, Clock, Users, Luggage, Check, ArrowLeft, ArrowRight, CheckCircle2, Repeat, Pencil, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import BookingWidget from "../components/site/BookingWidget";
import { FLEET, fareInfo, CONTACT, routePoints, positionedFare, geoOf, registerGeo, vehicleAlt, needsPin, pinPlace } from "../mock";
import { RouteMap } from "../components/site/RouteMap";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { api } from "../lib/api";

const STEPS = ["Vehicle", "Details", "Confirmed"];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-10" data-testid="booking-stepper">
    {STEPS.map((s, i) => (
      <React.Fragment key={s}>
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full text-[12px] sm:text-[13px] font-semibold flex items-center justify-center transition-colors ${i <= step ? "bg-[#183C35] text-[#F7F3EA]" : "bg-white border border-[#183C35]/20 text-[#183C35]/60"}`}>
            {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </span>
          <span className={`text-[12.5px] sm:text-[14.5px] ${i <= step ? "text-[#183C35] font-medium" : "text-[#202522]/50"}`}>{s}</span>
        </div>
        {i < STEPS.length - 1 && <span className={`flex-1 max-w-[40px] sm:max-w-[80px] h-px ${i < step ? "bg-[#183C35]" : "bg-[#183C35]/15"}`} />}
      </React.Fragment>
    ))}
  </div>
);

const Row = ({ icon: I, children }) => (
  <p className="flex items-start gap-2.5 text-[14.5px] text-[#202522]/85"><I className="w-4 h-4 mt-0.5 text-[#3E7F8E] shrink-0" /><span>{children}</span></p>
);

const fmt = (d) => { try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; } };

const MobileBarSpacer = () => {
  useEffect(() => {
    document.body.classList.add("has-mobile-bar");
    return () => document.body.classList.remove("has-mobile-bar");
  }, []);
  return null;
};

const Booking = () => {
  const [sp] = useSearchParams();
  const q = Object.fromEntries(sp.entries());
  // links from emails carry place area/coords so prices are right on any device
  if (q.pgeo) registerGeo(q.pickup, q.pgeo);
  if (q.dgeo) registerGeo(q.dropoff, q.dgeo);
  const ready = q.pickup && q.date && q.time && (q.dropoff || q.hours);
  const pax = Number(q.pax || 2);
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState(null);
  const [extras, setExtras] = useState({ child: false, booster: false, sign: true });
  const [pay, setPay] = useState("driver");
  const [f, setF] = useState({ name: "", email: "", phone: "", flight: "", notes: "" });
  const [ref, setRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState((sp.get("promo") || "").toUpperCase());
  const [promo, setPromo] = useState(null); // { code, total, discount, forTotal }
  const [promoMsg, setPromoMsg] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [paidTotal, setPaidTotal] = useState(null);

  const est = useMemo(() => fareInfo(q.pickup, q.dropoff), [q.pickup, q.dropoff]);
  const [route, setRoute] = useState(null); // { km, minutes, legs, leg_minutes, source }
  const [routeBusy, setRouteLoading] = useState(false);
  const [mapRoute, setMapRoute] = useState(null); // client's own A -> B route with line
  const [pinning, setPinning] = useState(() => [q.pickup, q.dropoff].some((n) => n && needsPin(n)));
  const [geoVer, setGeoVer] = useState(0);
  useEffect(() => {
    const need = [q.pickup, q.dropoff].filter((n) => n && needsPin(n));
    if (!need.length) { setPinning(false); return; }
    let cancelled = false;
    setPinning(true);
    Promise.all(need.map((n) => api.get("/geocode", { params: { name: n }, timeout: 7000 })
      .then((r) => { if (r.data.found) pinPlace(n, r.data.lat, r.data.lon); }).catch(() => {})))
      .finally(() => { if (!cancelled) { setPinning(false); setGeoVer((v) => v + 1); } });
    return () => { cancelled = true; };
  }, [q.pickup, q.dropoff]);
  const routeLoading = routeBusy || (pinning && est.positioned);
  useEffect(() => {
    if (!ready || !q.dropoff || pinning) return;
    const pts = routePoints(q.pickup, q.dropoff, est.positioned);
    const ppts = routePoints(q.pickup, q.dropoff, est.positioned, true);
    if (!pts) return;
    let cancelled = false;
    setRouteLoading(est.positioned);
    const enc = (list) => list.map((x) => x.join(",")).join(";");
    const get = (list) => api.get("/route", { params: { points: enc(list) }, timeout: 8000 }).then((r) => r.data);
    const same = enc(pts) === enc(ppts);
    const mpts = routePoints(q.pickup, q.dropoff, false);
    api.get("/route", { params: { points: enc(mpts), geometry: true }, timeout: 10000 })
      .then((r) => { if (!cancelled) setMapRoute({ ...r.data, a: mpts[0], b: mpts[1] }); }).catch(() => {});
    Promise.all([get(pts), same || !est.positioned ? null : get(ppts)])
      .then(([real, pricing]) => { if (!cancelled) setRoute({ ...real, priceKm: (pricing || real).km }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRouteLoading(false); });
    return () => { cancelled = true; };
  }, [q.pickup, q.dropoff, est.positioned, ready, pinning, geoVer]);
  const fi = est.positioned && route ? { ...est, fare: positionedFare(route.priceKm ?? route.km, est.p, est.d), km: route.km } : est;
  const tripMinutes = mapRoute ? mapRoute.minutes : route ? route.leg_minutes?.[route.leg_minutes.length - 1] : null;
  const tripKm = mapRoute ? mapRoute.km : route?.legs ? route.legs[route.legs.length - 1] : null;
  const [showMap, setShowMap] = useState(false);
  const base = fi.fare;
  const priceFor = (v) => {
    if (q.type === "hourly") return Number(q.hours) * (38 + Math.round(v.add / 2));
    const one = base + v.add;
    return q.type === "round" ? one * 2 : one;
  };
  const extrasCost = (extras.child ? 0 : 0) + (extras.booster ? 0 : 0);
  const onRequest = q.type === "hourly";
  const subtotal = vehicle && !onRequest ? priceFor(vehicle) + extrasCost : 0;
  const promoActive = promo && promo.forTotal === subtotal;
  const total = promoActive ? promo.total : subtotal;

  const applyPromo = async (codeArg) => {
    const code = (codeArg ?? promoInput).trim();
    if (!code) { setPromoMsg({ ok: false, text: "Please enter a code." }); return; }
    if (!subtotal) { setPromoMsg({ ok: false, text: "Choose a vehicle first." }); return; }
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code, total: subtotal, type: q.type || "oneway" });
      if (data.valid) { setPromo({ code: data.code, total: data.total, discount: data.discount, forTotal: subtotal }); setPromoInput(data.code); }
      else setPromo(null);
      setPromoMsg({ ok: data.valid, text: data.message });
    } catch { setPromoMsg({ ok: false, text: "Could not check the code. Please try again." }); }
    finally { setPromoLoading(false); }
  };
  // re-check when price changes (different vehicle) or auto-apply code from email link
  useEffect(() => {
    if (!subtotal) return;
    if (promo && promo.forTotal !== subtotal) applyPromo(promo.code);
    else if (!promo && q.promo && !promoMsg) applyPromo(q.promo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);
  const waLink = (v) => {
    const msg = [
      "Hello Aloha Corfu Transfers, I would like a price for an hourly chauffeur booking:",
      v ? `Vehicle: ${v.name} (${v.model})` : null,
      `Pickup: ${q.pickup}`,
      `Duration: ${q.hours} hours`,
      `Date: ${fmt(q.date)} at ${q.time}`,
      `Passengers: ${pax} · Luggage: ${q.bags}`,
    ].filter(Boolean).join("\n");
    return `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent(msg)}`;
  };
  const logWa = (v) => {
    api.post("/enquiries/hourly", { pickup: q.pickup, hours: String(q.hours), date: q.date, time: q.time, pax, bags: Number(q.bags || 0), vehicle: v ? v.name : null }).catch(() => {});
  };
  const priceText = (v) => (onRequest ? "Price on request" : routeLoading ? "…" : `€${priceFor(v)}`);

  if (!ready) {
    return (
      <div data-testid="booking-start-page">
        <section className="bg-[#1A3C37]">
          <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1fr_572px] gap-10 items-center">
            <div className="fade-up">
              <p className="text-[13px] tracking-[0.25em] uppercase text-[#7FB8C4]">Book your transfer</p>
              <h1 className="font-serif-d font-bold text-[42px] sm:text-[54px] text-[#F7F3EA] mt-3 leading-tight">Where are we taking you?</h1>
              <p className="mt-4 text-[17px] text-[#F7F3EA]/80 max-w-md">Enter your trip details to see fixed prices for every vehicle class. No hidden fees, free cancellation up to 24h.</p>
              {q.vehicle && <p className="mt-4 inline-block rounded-full bg-[#E3CBA5] text-[#183C35] text-[13px] font-medium px-3 py-1">Selected class: {FLEET.find((v) => v.id === q.vehicle)?.name}</p>}
            </div>
            <BookingWidget preset={{ pickup: q.pickup, dropoff: q.dropoff, type: q.type, pax: q.pax, bags: q.bags }} />
          </div>
        </section>
        <div className="h-24" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) return toast.error("Please fill in your name, email and phone.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        type: q.type || "oneway", pickup: q.pickup, dropoff: q.dropoff || null, date: q.date, time: q.time,
        rdate: q.rdate || null, rtime: q.rtime || null, hours: q.hours || null, pax, bags: Number(q.bags || 0),
        vehicle_id: vehicle.id, vehicle: `${vehicle.name} (${vehicle.model})`, total: subtotal,
        promo_code: promoActive ? promo.code : null,
        route_km: route ? route.km : null, route_minutes: tripMinutes || null,
        pickup_geo: geoOf(q.pickup), dropoff_geo: q.dropoff ? geoOf(q.dropoff) : null,
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), flight: f.flight, notes: f.notes, extras, pay,
      });
      if (pay === "card") {
        const { data: co } = await api.post("/payments/checkout", { ref: data.ref, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      setRef(data.ref);
      setPaidTotal(data.total);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? "Please check your email address and details." : "Could not complete booking. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  const Summary = (
    <aside className={`${step === 2 ? "" : "hidden lg:block"} lg:sticky lg:top-24 bg-white rounded-2xl border border-[#183C35]/10 p-5 sm:p-6 space-y-3`} data-testid="trip-summary">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-d text-[22px] text-[#183C35]">Your trip</h3>
        {step < 2 && <Link to={`/book?type=${q.type || "oneway"}&pickup=${encodeURIComponent(q.pickup)}${q.dropoff ? `&dropoff=${encodeURIComponent(q.dropoff)}` : ""}`} className="flex items-center gap-1 text-[13px] text-[#3E7F8E] hover:text-[#183C35]" data-testid="edit-trip"><Pencil className="w-3.5 h-3.5" />Edit</Link>}
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F3EA] px-3 py-1 text-[12.5px] text-[#183C35]">
        {q.type === "round" ? <><Repeat className="w-3.5 h-3.5" />Round trip</> : q.type === "hourly" ? <><Clock className="w-3.5 h-3.5" />By the hour</> : <><ArrowRight className="w-3.5 h-3.5" />One way</>}
      </span>
      <Row icon={Plane}>{q.pickup}</Row>
      {q.dropoff ? <Row icon={MapPin}>{q.dropoff}</Row> : <Row icon={Clock}>{q.hours} hours with chauffeur</Row>}
      <Row icon={Calendar}>{fmt(q.date)} · {q.time}</Row>
      {tripMinutes ? <Row icon={Clock}><span data-testid="trip-drive-time">Approx. {tripMinutes} min drive{tripKm ? ` · ${tripKm} km` : ""}</span></Row> : null}
      {mapRoute && <div className="hidden lg:block pt-1"><RouteMap a={mapRoute.a} b={mapRoute.b} line={mapRoute.geometry} className="h-56" testid="route-map-desktop" /></div>}
      {q.type === "round" && <Row icon={Repeat}>Return {fmt(q.rdate)} · {q.rtime}</Row>}
      <div className="flex gap-5"><Row icon={Users}>{pax} passengers</Row><Row icon={Luggage}>{q.bags} bags</Row></div>
      {onRequest && (
        <div className="border-t border-[#183C35]/10 pt-4 mt-2">
          <div className="flex justify-between items-end"><span className="text-[14px] text-[#202522]/65">Total</span><span className="font-serif-d text-[20px] text-[#183C35] leading-none" data-testid="booking-total">Price on request</span></div>
          <a href={waLink(null)} target="_blank" rel="noreferrer" onClick={() => logWa(null)} data-testid="summary-whatsapp-price"
            className="mt-4 w-full h-12 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#1FBF5B] transition-colors duration-200">
            <MessageCircle className="w-4 h-4" />Ask for price on WhatsApp
          </a>
          <p className="text-[12px] text-[#202522]/55 mt-2">Opens WhatsApp with your trip details ready to send.</p>
        </div>
      )}
      {vehicle && !onRequest && (
        <div className="border-t border-[#183C35]/10 pt-4 mt-2">
          <div className="flex justify-between text-[14.5px]"><span>{vehicle.name}</span><span>{priceText(vehicle)}</span></div>
          {(extras.child || extras.booster) && <div className="flex justify-between text-[13.5px] text-[#202522]/65 mt-1"><span>Child seats</span><span>Free</span></div>}
          {promoActive && promo.discount > 0 && <div className="flex justify-between text-[13.5px] text-[#1C7A43] mt-1" data-testid="summary-discount"><span>Discount ({promo.code})</span><span>-€{promo.discount}</span></div>}
          <div className="flex justify-between items-end mt-3"><span className="text-[14px] text-[#202522]/65">Total</span><span className={`font-serif-d text-[#183C35] leading-none ${onRequest ? "text-[20px]" : "text-[30px]"}`} data-testid="booking-total">{onRequest ? "Price on request" : <>{promoActive && promo.discount > 0 && <span className="text-[16px] text-[#202522]/40 line-through mr-2">€{subtotal}</span>}€{step === 2 && paidTotal != null ? paidTotal : total}</>}</span></div>
          <p className="text-[12px] text-[#202522]/55 mt-1">Fixed price per vehicle · taxes & tolls included</p>
          {fi.positioned && <p className="text-[12px] text-[#202522]/55 mt-1" data-testid="positioning-note">Includes driver positioning from Corfu Airport to your pickup{route?.source === "road" ? ` (${route.km} km total by road)` : ""}.</p>}
        </div>
      )}
    </aside>
  );

  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-14 ${step < 2 && !onRequest ? "pb-28 lg:pb-14" : ""}`} data-testid="booking-page">
      <Stepper step={step} />
      {step < 2 && (
        <div className="lg:hidden -mt-2 mb-4 rounded-xl bg-white border border-[#183C35]/10 px-3.5 py-2.5 flex items-start justify-between gap-3" data-testid="mobile-trip-bar">
          <div className="min-w-0 text-[13px] leading-snug">
            <p className="font-medium text-[#183C35] truncate">{q.pickup}</p>
            <p className="text-[#202522]/70 truncate">→ {q.dropoff || `${q.hours} hours with chauffeur`}</p>
            <p className="text-[#202522]/55 mt-0.5">{fmt(q.date)} · {q.time} · {pax} pax · {q.bags} bags{tripMinutes ? ` · ~${tripMinutes} min` : ""}{tripKm ? ` · ${tripKm} km` : ""}</p>
            {mapRoute && <button type="button" onClick={() => setShowMap((v) => !v)} className="mt-1.5 -ml-1 px-1 py-1 inline-flex items-center gap-1 text-[13px] font-medium text-[#3E7F8E]" data-testid="toggle-route-map"><MapPin className="w-3.5 h-3.5" />{showMap ? "Hide map" : "View route on map"}</button>}
          </div>
          <Link to={`/book?type=${q.type || "oneway"}&pickup=${encodeURIComponent(q.pickup)}${q.dropoff ? `&dropoff=${encodeURIComponent(q.dropoff)}` : ""}`} className="shrink-0 -m-2 p-2 flex items-center gap-1 text-[13px] text-[#3E7F8E]" data-testid="mobile-trip-edit"><Pencil className="w-3.5 h-3.5" />Edit</Link>
        </div>
      )}
      {step < 2 && mapRoute && showMap && <div className="lg:hidden -mt-2 mb-4"><RouteMap a={mapRoute.a} b={mapRoute.b} line={mapRoute.geometry} className="h-60" testid="route-map-mobile" /></div>}
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-10 items-start">
        <div>
          {step === 0 && (
            <div className="fade-up">
              <h1 className="font-serif-d text-[24px] sm:text-[36px] font-semibold text-[#183C35]">Choose your vehicle</h1>
              <p className="mt-1 sm:mt-2 text-[13.5px] sm:text-[16px] text-[#202522]/70">{onRequest ? "Hourly chauffeur prices are on request — message us on WhatsApp for your vehicle and we'll send you a tailored quote." : "All prices are fixed, per vehicle, with free flight monitoring and meet & greet."}</p>
              <div className="mt-4 sm:mt-8 space-y-3 sm:space-y-4">
                {FLEET.map((v) => {
                  const ok = v.maxPax >= pax;
                  const sel = vehicle?.id === v.id;
                  return (
                    <div key={v.id} data-testid={`vehicle-option-${v.id}`} onClick={() => { if (ok && !onRequest && window.innerWidth < 640) setVehicle(v); }}
                      className={`grid grid-cols-[100px_1fr] sm:grid-cols-[220px_1fr_auto] gap-x-3 gap-y-2 sm:gap-5 items-center bg-white rounded-2xl border p-3 sm:p-4 transition-[border-color,box-shadow] duration-200 ${sel ? "border-[#183C35] shadow-[0_14px_30px_-18px_rgba(24,60,53,.5)]" : "border-[#183C35]/10"} ${ok ? "" : "opacity-55"}`}>
                      <img src={v.image} alt={vehicleAlt(v)} className="w-full h-[72px] sm:h-36 object-cover rounded-lg sm:rounded-xl bg-[#F1ECE0]" />
                      <div>
                        <h3 className="font-serif-d text-[18px] sm:text-[22px] text-[#183C35] leading-tight">{v.name}</h3>
                        <p className="text-[12px] sm:text-[13px] text-[#202522]/60">{v.model}</p>
                        <div className="flex gap-4 sm:gap-5 mt-1 sm:mt-2 text-[13px] sm:text-[14.5px]"><span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#3E7F8E]" />{v.pax}</span><span className="flex items-center gap-1.5"><Luggage className="w-4 h-4 text-[#3E7F8E]" />{v.bags}</span></div>
                        <ul className="hidden sm:grid mt-2 grid-cols-2 gap-x-4 gap-y-1">{v.features.slice(0, 4).map((x) => <li key={x} className="flex gap-1.5 text-[13px] text-[#202522]/75"><Check className="w-3.5 h-3.5 mt-0.5 text-[#3E7F8E]" />{x}</li>)}</ul>
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex sm:block items-center justify-between gap-3 sm:text-right border-t sm:border-0 border-[#183C35]/10 pt-2 sm:pt-0">
                        <div>
                        <p className={`font-serif-d text-[#183C35] leading-none ${onRequest ? "text-[17px] sm:text-[19px]" : "text-[24px] sm:text-[30px]"}`} data-testid={`vehicle-price-${v.id}`}>{priceText(v)}</p>
                        <p className="text-[11.5px] sm:text-[12px] text-[#202522]/55 mt-1">{q.type === "round" ? "round trip" : q.type === "hourly" ? `${q.hours} hours` : "one way"}</p>
                        </div>
                        {ok && onRequest ? (
                          <a href={waLink(v)} target="_blank" rel="noreferrer" onClick={() => logWa(v)} data-testid={`whatsapp-price-${v.id}`}
                            className="sm:mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 sm:px-5 py-2 sm:py-2.5 text-[13px] sm:text-[14.5px] font-semibold hover:bg-[#1FBF5B] transition-colors duration-200">
                            <MessageCircle className="w-4 h-4" />Get price on WhatsApp
                          </a>
                        ) : ok ? (
                          <button onClick={(e) => { e.stopPropagation(); setVehicle(v); }} data-testid={`select-vehicle-${v.id}`}
                            className={`sm:mt-3 rounded-full px-5 h-11 sm:h-auto sm:py-2.5 text-[14.5px] sm:text-[14.5px] font-medium transition-colors duration-200 ${sel ? "bg-[#183C35] text-[#F7F3EA]" : "bg-[#F7F3EA] text-[#183C35] hover:bg-[#EDE4D2]"}`}>
                            {sel ? "Selected" : "Select"}
                          </button>
                        ) : <p className="mt-3 text-[12.5px] text-[#B42318]">Not suitable for {pax} passengers</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {onRequest ? (
                <p className="mt-5 sm:mt-8 text-[13.5px] sm:text-[14.5px] text-[#202522]/70" data-testid="hourly-wa-note">Tap <strong>Get price on WhatsApp</strong> on your preferred vehicle — your trip details are sent to us automatically and we'll reply with a quote within minutes.</p>
              ) : (
              <button disabled={!vehicle || routeLoading} onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="continue-to-details"
                className="hidden lg:inline-flex mt-8 items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-7 py-3.5 font-semibold hover:bg-[#21524A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
              )}
            </div>
          )}

          {step === 1 && (
            <form id="details-form-el" onSubmit={submit} className="fade-up" data-testid="details-form">
              <button type="button" onClick={() => setStep(0)} className="-ml-1 py-2 px-1 flex items-center gap-1.5 text-[14.5px] text-[#3E7F8E] hover:text-[#183C35]" data-testid="back-to-vehicles"><ArrowLeft className="w-4 h-4" />Back to vehicles</button>
              <h1 className="font-serif-d text-[24px] sm:text-[36px] font-semibold text-[#183C35] mt-2 sm:mt-3">Passenger details</h1>
              <div className="bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6 mt-4 sm:mt-6 grid sm:grid-cols-2 gap-3 sm:gap-4">
                <Input className="h-11 sm:h-12 rounded-xl" placeholder="Full name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="details-name" />
                <Input className="h-11 sm:h-12 rounded-xl" type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="details-email" />
                <Input className="h-11 sm:h-12 rounded-xl" placeholder="Mobile / WhatsApp *" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="details-phone" />
                <Input className="h-11 sm:h-12 rounded-xl" placeholder="Flight / ferry number" value={f.flight} onChange={(e) => setF({ ...f, flight: e.target.value })} data-testid="details-flight" />
                <Textarea className="sm:col-span-2 rounded-xl" rows={3} placeholder="Notes for your driver (villa gate code, special requests...)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
              </div>
              <h3 className="font-serif-d text-[20px] sm:text-[22px] text-[#183C35] mt-6 sm:mt-8">Extras</h3>
              <div className="bg-white rounded-2xl border border-[#183C35]/10 px-4 py-2 sm:p-6 mt-3 space-y-0 sm:space-y-4">
                {[["child", "Child seat (0–4 yrs)", "Free"], ["booster", "Booster seat (4–12 yrs)", "Free"], ["sign", "Meet & greet name sign", "Included"]].map(([k, l, p]) => (
                  <label key={k} className="flex items-center justify-between gap-3 min-h-[44px] cursor-pointer">
                    <span className="flex items-center gap-3 text-[15px]"><Checkbox className="h-5 w-5" checked={extras[k]} onCheckedChange={(c) => setExtras({ ...extras, [k]: !!c })} data-testid={`extra-${k}`} />{l}</span>
                    <span className="text-[13.5px] text-[#202522]/60">{p}</span>
                  </label>
                ))}
              </div>
              <h3 className="font-serif-d text-[20px] sm:text-[22px] text-[#183C35] mt-6 sm:mt-8">Discount code</h3>
              <div className="bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6 mt-3" data-testid="promo-box">
                <div className="flex gap-3">
                  <Input className="h-12 rounded-xl uppercase tracking-wider" placeholder="e.g. BACK-AB12C" value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoMsg(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }} data-testid="promo-input" />
                  {promoActive ? (
                    <button type="button" onClick={() => { setPromo(null); setPromoMsg(null); setPromoInput(""); }} data-testid="promo-remove"
                      className="shrink-0 h-12 px-5 rounded-xl border border-[#183C35]/20 text-[#183C35] text-[14.5px] font-medium hover:bg-[#F7F3EA] transition-colors">Remove</button>
                  ) : (
                    <button type="button" onClick={() => applyPromo()} disabled={promoLoading} data-testid="promo-apply"
                      className="shrink-0 h-12 px-6 rounded-xl bg-[#183C35] text-[#F7F3EA] text-[14.5px] font-semibold hover:bg-[#21524A] disabled:opacity-60 transition-colors">{promoLoading ? "Checking..." : "Apply"}</button>
                  )}
                </div>
                {promoMsg && <p className={`mt-3 text-[13.5px] ${promoMsg.ok ? "text-[#1C7A43]" : "text-[#B42318]"}`} data-testid="promo-message">{promoMsg.text}</p>}
                <p className="mt-2 text-[12px] text-[#202522]/50">10% off · our lowest fare of €36 per ride always applies.</p>
              </div>
              <h3 className="font-serif-d text-[20px] sm:text-[22px] text-[#183C35] mt-6 sm:mt-8">Payment</h3>
              <RadioGroup value={pay} onValueChange={setPay} className="mt-3 grid sm:grid-cols-2 gap-3">
                {[["driver", "Pay your driver", "Cash or card on the day"], ["card", "Pay by card online", "Visa, Mastercard, Apple Pay or Google Pay"]].map(([k, l, d]) => (
                  <label key={k} className={`flex items-start gap-3 bg-white rounded-2xl border p-4 sm:p-5 cursor-pointer transition-colors ${pay === k ? "border-[#183C35]" : "border-[#183C35]/10"}`} data-testid={`pay-${k}`}>
                    <RadioGroupItem value={k} className="mt-0.5 h-5 w-5" />
                    <span><span className="block text-[15px] font-medium text-[#183C35]">{l}</span><span className="block text-[13px] text-[#202522]/60">{d}</span></span>
                  </label>
                ))}
              </RadioGroup>
              <button type="submit" disabled={submitting} data-testid="confirm-booking-btn" className="disabled:opacity-60 mt-6 sm:mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-8 py-4 font-semibold hover:bg-[#21524A] transition-colors duration-200">
                {submitting ? (pay === "card" ? "Opening secure payment..." : "Confirming...") : pay === "card" ? `Pay €${total} by card` : `Confirm booking · €${total}`}
              </button>
              <p className="mt-3 text-[12.5px] text-[#202522]/55">By confirming you agree to our <Link to="/legal/terms" className="underline">terms</Link> and <Link to="/legal/cancellation" className="underline">cancellation policy</Link>.</p>
            </form>
          )}

          {step === 2 && (
            <div className="fade-up bg-white rounded-2xl border border-[#183C35]/10 p-6 sm:p-12 text-center" data-testid="booking-confirmed">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[#3E7F8E]" />
              <h1 className="font-serif-d text-[28px] sm:text-[38px] leading-tight font-semibold text-[#183C35] mt-4">{onRequest ? "Request received!" : "Booking confirmed!"}</h1>
              <p className="mt-2 text-[15px] sm:text-[16px] text-[#202522]/70">Thank you, {f.name.split(" ")[0]}. A confirmation email is on its way to {f.email}. Please check your spam folder if you don't see it in a few minutes.</p>
              <p className="mt-6 inline-block rounded-full bg-[#F7F3EA] px-5 py-2 text-[15px] text-[#183C35]">Reference: <strong data-testid="booking-ref">{ref}</strong></p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/" className="rounded-full bg-[#183C35] text-[#F7F3EA] px-6 py-3 font-medium hover:bg-[#21524A] transition-colors">Back to home</Link>
                <Link to="/tours" className="rounded-full border border-[#183C35]/20 text-[#183C35] px-6 py-3 font-medium hover:bg-[#F7F3EA] transition-colors">Explore tours</Link>
              </div>
            </div>
          )}
        </div>
        {Summary}
      </div>
      {step < 2 && !onRequest && <MobileBarSpacer />}
      {step < 2 && !onRequest && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#183C35]/10 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] flex items-center justify-between gap-3 shadow-[0_-10px_30px_-15px_rgba(24,60,53,.35)]" data-testid="mobile-cta-bar">
          <div className="min-w-0">
            <p className="text-[12px] text-[#202522]/60 truncate">{vehicle ? vehicle.name : "Select a vehicle"}</p>
            <p className="font-serif-d text-[22px] text-[#183C35] leading-none" data-testid="mobile-total">
              {vehicle ? <>{promoActive && promo.discount > 0 && <span className="text-[13px] text-[#202522]/40 line-through mr-1.5">€{subtotal}</span>}€{routeLoading ? "…" : total}</> : "—"}
            </p>
          </div>
          {step === 0 ? (
            <button key="m-continue" type="button" disabled={!vehicle || routeLoading} onClick={(e) => { e.preventDefault(); setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="mobile-continue"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-6 py-3 text-[15px] font-semibold disabled:opacity-40 transition-colors">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button key="m-confirm" type="submit" form="details-form-el" disabled={submitting} data-testid="mobile-confirm"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-6 py-3 text-[15px] font-semibold disabled:opacity-60 transition-colors">
              {submitting ? (pay === "card" ? "Opening payment..." : "Confirming...") : pay === "card" ? "Pay by card" : "Confirm booking"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Booking;
